/**
 * FlowComponentRegistry - Central registry for all WhatsApp Flow components
 *
 * Manages flow components and provides lookup/query functionality.
 * Singleton pattern ensures single source of truth for all components.
 */

import { FlowComponent } from './FlowComponent';

class FlowComponentRegistry {
  constructor() {
    this.components = new Map();       // type -> component
    this.categories = new Map();       // category -> [components]
    this.initialized = false;
  }

  /**
   * Register a flow component
   * @param {FlowComponent} component - Component instance to register
   * @throws {Error} If component is invalid or already registered
   */
  register(component) {
    // Validate component
    if (!(component instanceof FlowComponent)) {
      throw new Error('Component must be an instance of FlowComponent');
    }

    if (!component.type) {
      throw new Error('Component must have a type');
    }

    if (this.components.has(component.type)) {
      console.warn(`Component ${component.type} already registered, replacing...`);
    }

    // Register component
    this.components.set(component.type, component);

    // Add to category
    if (!this.categories.has(component.category)) {
      this.categories.set(component.category, []);
    }

    const categoryComponents = this.categories.get(component.category);
    const existingIndex = categoryComponents.findIndex(c => c.type === component.type);

    if (existingIndex >= 0) {
      // Replace existing
      categoryComponents[existingIndex] = component;
    } else {
      // Add new
      categoryComponents.push(component);
    }

    console.log(`✓ Registered flow component: ${component.type} (${component.category})`);
  }

  /**
   * Register multiple components at once
   * @param {FlowComponent[]} components - Array of components to register
   */
  registerAll(components) {
    components.forEach(component => this.register(component));
    this.initialized = true;
    console.log(`✓ Registered ${components.length} flow components`);
  }

  /**
   * Unregister a flow component
   * @param {string} type - Component type to unregister
   */
  unregister(type) {
    const component = this.components.get(type);

    if (component) {
      // Remove from components map
      this.components.delete(type);

      // Remove from category
      const categoryComponents = this.categories.get(component.category);
      if (categoryComponents) {
        const index = categoryComponents.findIndex(c => c.type === type);
        if (index >= 0) {
          categoryComponents.splice(index, 1);
        }
      }

      console.log(`✓ Unregistered flow component: ${type}`);
    }
  }

  /**
   * Get a specific component by type
   * @param {string} type - Component type
   * @returns {FlowComponent|undefined}
   */
  get(type) {
    return this.components.get(type);
  }

  /**
   * Get all registered components
   * @returns {FlowComponent[]}
   */
  getAll() {
    return Array.from(this.components.values());
  }

  /**
   * Get components by category
   * @param {string} category - Category name ('display', 'input', etc.)
   * @returns {FlowComponent[]}
   */
  getByCategory(category) {
    return this.categories.get(category) || [];
  }

  /**
   * Get all categories
   * @returns {string[]}
   */
  getCategories() {
    return Array.from(this.categories.keys());
  }

  /**
   * Check if a component type is registered
   * @param {string} type - Component type
   * @returns {boolean}
   */
  has(type) {
    return this.components.has(type);
  }

  /**
   * Get total number of registered components
   * @returns {number}
   */
  count() {
    return this.components.size;
  }

  /**
   * Get components that can be added to a specific screen
   * @param {Object} screen - Screen object with components array
   * @returns {FlowComponent[]}
   */
  getAvailableForScreen(screen) {
    return this.getAll().filter(component => component.canAddToScreen(screen));
  }

  /**
   * Find components by predicate function
   * @param {Function} predicate - Function that takes component and returns boolean
   * @returns {FlowComponent[]}
   */
  find(predicate) {
    return this.getAll().filter(predicate);
  }

  /**
   * Get component metadata summary
   * @returns {Object} Summary of registered components
   */
  getSummary() {
    const summary = {
      total: this.count(),
      categories: {},
      components: []
    };

    this.getCategories().forEach(category => {
      const components = this.getByCategory(category);
      summary.categories[category] = components.length;
      summary.components.push(...components.map(c => ({
        type: c.type,
        name: c.name,
        category: c.category,
        maxPerScreen: c.maxPerScreen
      })));
    });

    return summary;
  }

  /**
   * Validate that all expected component types are registered
   * @param {string[]} expectedTypes - Array of component types that should be registered
   * @returns {Object} {valid: boolean, missing: string[]}
   */
  validateRegistration(expectedTypes) {
    const missing = expectedTypes.filter(type => !this.has(type));

    return {
      valid: missing.length === 0,
      missing
    };
  }

  /**
   * Clear all registered components (mainly for testing)
   */
  clear() {
    this.components.clear();
    this.categories.clear();
    this.initialized = false;
    console.log('✓ Cleared all flow components');
  }

  /**
   * Get registry statistics
   * @returns {Object}
   */
  getStats() {
    const stats = {
      totalComponents: this.count(),
      categories: {},
      componentsWithMaxLimit: 0,
      averagePropertiesPerComponent: 0
    };

    this.getCategories().forEach(category => {
      stats.categories[category] = this.getByCategory(category).length;
    });

    this.getAll().forEach(component => {
      if (component.maxPerScreen !== null && component.maxPerScreen !== undefined) {
        stats.componentsWithMaxLimit++;
      }
    });

    return stats;
  }
}

// Export singleton instance
export const flowComponentRegistry = new FlowComponentRegistry();

// Export class for testing
export { FlowComponentRegistry };
